import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getSocietyAdminData,
  getAdminSocietyDetail,
  getSocietyLedgerBalances,
  getFlatMembers,
  flatMemberRemovalCheck,
  removeFlatMember,
  adminAddFlatMember,
  downloadSocietyBillsReportFile,
  downloadSocietyAuditReportFile,
  getEnrichedPendingBillPayments,
  getMaintenanceTracking,
  getSocietySettings,
  getFlatDuplicatePreview,
  mergeFlatDuplicateGroup,
  mergeAllFlatDuplicateGroups,
  getCompoundFlatPreview,
  splitCompoundFlat,
  splitAllCompoundFlats,
  AdminSocietyDetail,
  AdminSocietyFlat,
  FlatMemberInfo,
  FlatMemberRole,
  FlatDuplicateGroupPreview,
  CompoundFlatPreview,
  RemovalCheckData,
  CategoryLedgerBalance,
  MaintenanceTrackingData,
  PendingPaymentApprovalGroup,
} from '../services/api';
import { HiExclamation, HiRefresh, HiDownload, HiPlus, HiTrash, HiX, HiUsers } from '../components/Icons';
import './SocietyAdminOverview.css';
import './SocietyAdminFlats.css';

type FlatOccupancyFilter = 'all' | 'with_owner' | 'with_tenant' | 'vacant';
type RoleCategory = 'owner' | 'co_owner' | 'tenant' | 'family';

function normalizeRoleCategory(role?: string | null): RoleCategory {
  const r = (role || '').toLowerCase().replace(/[\s-]+/g, '_');
  if (r === 'co_owner') return 'co_owner';
  if (r === 'owner') return 'owner';
  if (r.includes('family')) return 'family';
  return 'tenant';
}

function roleBadgeLabel(cat: RoleCategory): string {
  if (cat === 'owner') return 'Owner';
  if (cat === 'co_owner') return 'Co-owner';
  if (cat === 'family') return 'Family';
  return 'Tenant';
}

function flatHasOwner(flat: AdminSocietyFlat): boolean {
  return (flat.users || []).some((u) => normalizeRoleCategory(u.role_in_flat) === 'owner');
}

function flatHasTenant(flat: AdminSocietyFlat): boolean {
  return (flat.users || []).some((u) => normalizeRoleCategory(u.role_in_flat) === 'tenant');
}

function flatHasCoOwner(flat: AdminSocietyFlat): boolean {
  return (flat.users || []).some((u) => normalizeRoleCategory(u.role_in_flat) === 'co_owner');
}

function flatHasOwnerAndCoOwner(flat: AdminSocietyFlat): boolean {
  return flatHasOwner(flat) && flatHasCoOwner(flat);
}

function flatHasTenantOnly(flat: AdminSocietyFlat): boolean {
  return flatHasTenant(flat) && !flatHasOwner(flat) && !flatHasCoOwner(flat);
}

function formatCompoundAction(action: string): string {
  if (action === 'skip_already_on_target') return 'Already on target flat — skip';
  if (action === 'assign_as_owner') return 'Assign as owner';
  if (action === 'assign_as_co_owner') return 'Assign as co-owner';
  if (action === 'assign_as_tenant') return 'Assign as tenant';
  if (action === 'assign_as_family_member') return 'Assign as family member';
  if (action === 'move_or_assign_first_target') return 'Move to first target flat';
  return action.replace(/_/g, ' ');
}

function formatMergeAction(action: string): string {
  if (action === 'deactivate_duplicate_membership') return 'Remove duplicate membership';
  if (action === 'move_as_owner') return 'Move as owner';
  if (action === 'move_as_co_owner') return 'Move as co-owner';
  if (action === 'move_as_tenant') return 'Move as tenant';
  if (action === 'move_as_family_member') return 'Move as family member';
  return action.replace(/_/g, ' ');
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatRupees(amount: number): string {
  return '\u20B9' + amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

const csvEscapeCell = (v: unknown): string => {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const defaultReportRange = (): { startDate: string; endDate: string } => {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: ymd(start), endDate: ymd(end) };
};

const formatCurrency = (amount: number): string =>
  '\u20B9' + amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

interface FlatDueSummary {
  flat: AdminSocietyFlat;
  totalDue: number;
  oldestDueDate: string | null;
  categories: string[];
}

const fetchAllFlatBalances = async (
  societyId: number
): Promise<Map<number, CategoryLedgerBalance[]>> => {
  const res = await getSocietyLedgerBalances(societyId);
  const map = new Map<number, CategoryLedgerBalance[]>();
  for (const item of res.data ?? []) {
    map.set(item.flat_id, item.balances ?? []);
  }
  return map;
};

const SocietyAdminOverview: React.FC = () => {
  const navigate = useNavigate();
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [loading, setLoading] = useState(true);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<AdminSocietyDetail | null>(null);

  const [flatBalances, setFlatBalances] = useState<Map<number, CategoryLedgerBalance[]> | null>(null);
  const [pendingPayments, setPendingPayments] = useState<PendingPaymentApprovalGroup[]>([]);
  const [maintenanceTracking, setMaintenanceTracking] = useState<MaintenanceTrackingData | null>(null);
  const [reconciliationEnabled, setReconciliationEnabled] = useState(false);

  const [flatFilter, setFlatFilter] = useState<FlatOccupancyFilter>('all');
  const [selectedFlat, setSelectedFlat] = useState<AdminSocietyFlat | null>(null);
  const [modalMembers, setModalMembers] = useState<FlatMemberInfo[]>([]);
  const [modalMembersLoading, setModalMembersLoading] = useState(false);
  const [modalMembersError, setModalMembersError] = useState('');

  const [addModal, setAddModal] = useState<{
    flat: AdminSocietyFlat;
    members: FlatMemberInfo[];
    phone: string;
    name: string;
    roleInFlat: FlatMemberRole;
    linkedToUserId: number | '';
    submitting: boolean;
    error: string;
  } | null>(null);

  const [removalModal, setRemovalModal] = useState<{
    flat: AdminSocietyFlat;
    member: FlatMemberInfo;
    checkData: RemovalCheckData | null;
    checkLoading: boolean;
    checkError: string;
    removing: boolean;
    reason: string;
  } | null>(null);

  const [reportRange, setReportRange] = useState(defaultReportRange);
  const [reportBusy, setReportBusy] = useState<string | null>(null);
  const [reportError, setReportError] = useState('');

  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState<FlatDuplicateGroupPreview[]>([]);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');
  const [mergingMasterFlatId, setMergingMasterFlatId] = useState<number | null>(null);
  const [mergingAll, setMergingAll] = useState(false);
  const [mergeSuccessMessage, setMergeSuccessMessage] = useState('');

  const [compoundModalOpen, setCompoundModalOpen] = useState(false);
  const [compoundFlats, setCompoundFlats] = useState<CompoundFlatPreview[]>([]);
  const [compoundLoading, setCompoundLoading] = useState(false);
  const [compoundError, setCompoundError] = useState('');
  const [splittingCompoundFlatId, setSplittingCompoundFlatId] = useState<number | null>(null);
  const [splittingAllCompounds, setSplittingAllCompounds] = useState(false);
  const [compoundSuccessMessage, setCompoundSuccessMessage] = useState('');

  const loadFinanceData = useCallback(async (flats: AdminSocietyFlat[]) => {
    if (!societyId || !flats.length) {
      setFlatBalances(new Map());
      setPendingPayments([]);
      setMaintenanceTracking(null);
      return;
    }
    setFinanceLoading(true);
    const now = new Date();
    try {
      const [balances, pendingRes, maintRes, settingsRes] = await Promise.all([
        fetchAllFlatBalances(societyId),
        getEnrichedPendingBillPayments(societyId).catch(() => ({ success: false, data: [] as PendingPaymentApprovalGroup[] })),
        getMaintenanceTracking(societyId, now.getMonth() + 1, now.getFullYear()).catch(() => null),
        getSocietySettings(societyId).catch(() => null),
      ]);
      setFlatBalances(balances);
      setPendingPayments(pendingRes.data ?? []);
      setMaintenanceTracking(maintRes?.success ? maintRes.data : null);
      if (settingsRes?.success && settingsRes.data) {
        setReconciliationEnabled(settingsRes.data.reconciliation_enabled === 'yes');
      }
    } finally {
      setFinanceLoading(false);
    }
  }, [societyId]);

  const refreshAll = useCallback(async () => {
    if (!societyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await getAdminSocietyDetail(societyId);
      setData(res.data);
      await loadFinanceData(res.data.flats ?? []);
    } catch (err: any) {
      setError(err.message || 'Failed to load society overview');
    } finally {
      setLoading(false);
    }
  }, [societyId, loadFinanceData]);

  const loadDuplicatePreview = useCallback(async () => {
    if (!societyId) return;
    setDuplicateLoading(true);
    setDuplicateError('');
    setMergeSuccessMessage('');
    try {
      const res = await getFlatDuplicatePreview(societyId);
      setDuplicateGroups(res.data ?? []);
    } catch (err: unknown) {
      setDuplicateGroups([]);
      setDuplicateError(err instanceof Error ? err.message : 'Failed to load duplicate flats');
    } finally {
      setDuplicateLoading(false);
    }
  }, [societyId]);

  const openDuplicateModal = useCallback(() => {
    setDuplicateModalOpen(true);
    void loadDuplicatePreview();
  }, [loadDuplicatePreview]);

  const handleMergeDuplicateGroup = useCallback(
    async (masterFlatId: number) => {
      if (!societyId) return;
      const confirmed = window.confirm(
        'Merge corrupted flat variants into the actual flat? Residents, tenants, family, and vehicles will move to the master flat.',
      );
      if (!confirmed) return;

      setMergingMasterFlatId(masterFlatId);
      setDuplicateError('');
      setMergeSuccessMessage('');
      try {
        const res = await mergeFlatDuplicateGroup(societyId, masterFlatId);
        setMergeSuccessMessage(res.message || 'Flat merged successfully');
        await loadDuplicatePreview();
        await refreshAll();
      } catch (err: unknown) {
        setDuplicateError(err instanceof Error ? err.message : 'Failed to merge flats');
      } finally {
        setMergingMasterFlatId(null);
      }
    },
    [societyId, loadDuplicatePreview, refreshAll],
  );

  const handleMergeAllDuplicateGroups = useCallback(async () => {
    if (!societyId || duplicateGroups.length === 0) return;
    const confirmed = window.confirm(
      `Merge all ${duplicateGroups.length} duplicate flat group(s)? Residents, tenants, family, and vehicles will move to the actual flats.`,
    );
    if (!confirmed) return;

    setMergingAll(true);
    setDuplicateError('');
    setMergeSuccessMessage('');
    try {
      const res = await mergeAllFlatDuplicateGroups(societyId);
      setMergeSuccessMessage(
        res.message ||
          `Merged ${res.data.totalGroups} group(s), removed ${res.data.totalDuplicatesRemoved} duplicate flat(s)`,
      );
      await loadDuplicatePreview();
      await refreshAll();
    } catch (err: unknown) {
      setDuplicateError(err instanceof Error ? err.message : 'Failed to merge all flats');
    } finally {
      setMergingAll(false);
    }
  }, [societyId, duplicateGroups.length, loadDuplicatePreview, refreshAll]);

  const loadCompoundPreview = useCallback(async () => {
    if (!societyId) return;
    setCompoundLoading(true);
    setCompoundError('');
    setCompoundSuccessMessage('');
    try {
      const res = await getCompoundFlatPreview(societyId);
      setCompoundFlats(res.data ?? []);
    } catch (err: unknown) {
      setCompoundFlats([]);
      setCompoundError(err instanceof Error ? err.message : 'Failed to load combined flats');
    } finally {
      setCompoundLoading(false);
    }
  }, [societyId]);

  const openCompoundModal = useCallback(() => {
    setCompoundModalOpen(true);
    void loadCompoundPreview();
  }, [loadCompoundPreview]);

  const handleSplitCompoundFlat = useCallback(
    async (compoundFlatId: number) => {
      if (!societyId) return;
      const confirmed = window.confirm(
        'Split this combined flat into separate master flats? Owners/co-owners will be linked to each unit; tenants/family go to the first unit.',
      );
      if (!confirmed) return;

      setSplittingCompoundFlatId(compoundFlatId);
      setCompoundError('');
      setCompoundSuccessMessage('');
      try {
        const res = await splitCompoundFlat(societyId, compoundFlatId);
        setCompoundSuccessMessage(res.message || 'Flat split successfully');
        await loadCompoundPreview();
        await refreshAll();
      } catch (err: unknown) {
        setCompoundError(err instanceof Error ? err.message : 'Failed to split flat');
      } finally {
        setSplittingCompoundFlatId(null);
      }
    },
    [societyId, loadCompoundPreview, refreshAll],
  );

  const handleSplitAllCompoundFlats = useCallback(async () => {
    if (!societyId || compoundFlats.length === 0) return;
    const splittable = compoundFlats.filter((c) => c.targets.every((t) => t.found));
    if (splittable.length === 0) {
      setCompoundError('No combined flats ready to split — fix missing master flats first.');
      return;
    }
    const confirmed = window.confirm(`Split all ${splittable.length} combined flat(s) into separate master flats?`);
    if (!confirmed) return;

    setSplittingAllCompounds(true);
    setCompoundError('');
    setCompoundSuccessMessage('');
    try {
      const res = await splitAllCompoundFlats(societyId);
      setCompoundSuccessMessage(
        res.message || `Split ${res.data.totalCompoundFlats} combined flat(s)`,
      );
      await loadCompoundPreview();
      await refreshAll();
    } catch (err: unknown) {
      setCompoundError(err instanceof Error ? err.message : 'Failed to split all flats');
    } finally {
      setSplittingAllCompounds(false);
    }
  }, [societyId, compoundFlats, loadCompoundPreview, refreshAll]);

  useEffect(() => {
    if (!societyId) {
      setLoading(false);
      setError('No society selected. Please select a society from the menu.');
      return;
    }
    void refreshAll();
  }, [societyId, refreshAll]);

  const flatFilterCounts = useMemo(() => {
    if (!data?.flats) return { all: 0, with_owner: 0, with_tenant: 0, vacant: 0 };
    let with_owner = 0;
    let with_tenant = 0;
    let vacant = 0;
    for (const flat of data.flats) {
      const hasUsers = (flat.users?.length || 0) > 0;
      if (!hasUsers) vacant += 1;
      if (flatHasOwner(flat)) with_owner += 1;
      if (flatHasTenant(flat)) with_tenant += 1;
    }
    return { all: data.flats.length, with_owner, with_tenant, vacant };
  }, [data]);

  const groupedFlats = useMemo(() => {
    if (!data?.flats) return {};
    const filtered = data.flats.filter((flat) => {
      const occupied = (flat.users?.length || 0) > 0;
      if (flatFilter === 'vacant') return !occupied;
      if (flatFilter === 'with_owner') return flatHasOwner(flat);
      if (flatFilter === 'with_tenant') return flatHasTenant(flat);
      return true;
    });

    const groups: Record<string, AdminSocietyFlat[]> = {};
    filtered.forEach((flat) => {
      const block = flat.block_name || 'Others';
      if (!groups[block]) groups[block] = [];
      groups[block].push(flat);
    });

    const sortedGroups: Record<string, AdminSocietyFlat[]> = {};
    Object.keys(groups)
      .sort()
      .forEach((block) => {
        sortedGroups[block] = groups[block].sort((a, b) => {
          if (a.floor !== b.floor) return (a.floor || 0) - (b.floor || 0);
          return a.flat_number.localeCompare(b.flat_number);
        });
      });
    return sortedGroups;
  }, [data, flatFilter]);

  const totalFilteredFlats = useMemo(
    () => Object.values(groupedFlats).reduce((sum, arr) => sum + arr.length, 0),
    [groupedFlats],
  );

  const loadModalMembers = useCallback(async (flatId: number) => {
    setModalMembersLoading(true);
    setModalMembersError('');
    try {
      const res = await getFlatMembers(flatId);
      setModalMembers(res.data);
    } catch (err: unknown) {
      setModalMembers([]);
      setModalMembersError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setModalMembersLoading(false);
    }
  }, []);

  const openFlatModal = useCallback(
    (flat: AdminSocietyFlat) => {
      setSelectedFlat(flat);
      void loadModalMembers(flat.id);
    },
    [loadModalMembers],
  );

  const closeFlatModal = () => {
    setSelectedFlat(null);
    setModalMembers([]);
    setModalMembersError('');
  };

  const refreshAfterMemberChange = async () => {
    if (!societyId) return;
    try {
      const res = await getAdminSocietyDetail(societyId);
      setData(res.data);
      if (selectedFlat) {
        const updated = res.data.flats.find((f) => f.id === selectedFlat.id);
        if (updated) setSelectedFlat(updated);
        await loadModalMembers(selectedFlat.id);
      }
    } catch {
      /* keep existing data */
    }
  };

  const openAddModal = () => {
    if (!selectedFlat) return;
    const hasOwner = modalMembers.some((m) => normalizeRoleCategory(m.roleInFlat) === 'owner');
    setAddModal({
      flat: selectedFlat,
      members: modalMembers,
      phone: '',
      name: '',
      roleInFlat: hasOwner ? 'tenant' : 'owner',
      linkedToUserId: '',
      submitting: false,
      error: '',
    });
  };

  const submitAddMember = async () => {
    if (!addModal) return;
    const phoneDigits = addModal.phone.replace(/\D/g, '').slice(-10);
    if (phoneDigits.length !== 10) {
      setAddModal((prev) => (prev ? { ...prev, error: 'Enter a valid 10-digit phone number' } : null));
      return;
    }
    if (!addModal.name.trim()) {
      setAddModal((prev) => (prev ? { ...prev, error: 'Name is required' } : null));
      return;
    }
    if (addModal.roleInFlat === 'family_member' && addModal.linkedToUserId === '') {
      setAddModal((prev) =>
        prev ? { ...prev, error: 'Select an owner or tenant to link this family member to' } : null,
      );
      return;
    }
    setAddModal((prev) => (prev ? { ...prev, submitting: true, error: '' } : null));
    try {
      const res = await adminAddFlatMember(addModal.flat.id, {
        phone: phoneDigits,
        name: addModal.name.trim(),
        roleInFlat: addModal.roleInFlat,
        linkedToUserId:
          addModal.roleInFlat === 'family_member' ? Number(addModal.linkedToUserId) : null,
      });
      if (!res.success) {
        setAddModal((prev) =>
          prev ? { ...prev, submitting: false, error: res.message || 'Failed to add member' } : null,
        );
        return;
      }
      setAddModal(null);
      await refreshAfterMemberChange();
    } catch (err: unknown) {
      setAddModal((prev) =>
        prev
          ? {
              ...prev,
              submitting: false,
              error: err instanceof Error ? err.message : 'Failed to add member',
            }
          : null,
      );
    }
  };

  const openRemovalModal = async (member: FlatMemberInfo) => {
    if (!selectedFlat) return;
    setRemovalModal({
      flat: selectedFlat,
      member,
      checkData: null,
      checkLoading: true,
      checkError: '',
      removing: false,
      reason: '',
    });
    try {
      const res = await flatMemberRemovalCheck(selectedFlat.id, member.userId);
      setRemovalModal((prev) =>
        prev ? { ...prev, checkData: res.data, checkLoading: false } : null,
      );
    } catch (err: unknown) {
      setRemovalModal((prev) =>
        prev
          ? {
              ...prev,
              checkLoading: false,
              checkError: err instanceof Error ? err.message : 'Failed to check removal',
            }
          : null,
      );
    }
  };

  const confirmRemoval = async () => {
    if (!removalModal?.checkData) return;
    setRemovalModal((prev) => (prev ? { ...prev, removing: true } : null));
    try {
      const res = await removeFlatMember(
        removalModal.flat.id,
        removalModal.member.userId,
        removalModal.reason || undefined,
      );
      if (!res.success) {
        setRemovalModal((prev) =>
          prev ? { ...prev, removing: false, checkError: res.message || 'Removal failed' } : null,
        );
        return;
      }
      setRemovalModal(null);
      await refreshAfterMemberChange();
    } catch (err: unknown) {
      setRemovalModal((prev) =>
        prev
          ? {
              ...prev,
              removing: false,
              checkError: err instanceof Error ? err.message : 'Removal failed',
            }
          : null,
      );
    }
  };

  const linkableMembers = useMemo(() => {
    if (!addModal) return [];
    return addModal.members.filter((m) => {
      const cat = normalizeRoleCategory(m.roleInFlat);
      return cat === 'owner' || cat === 'co_owner' || cat === 'tenant';
    });
  }, [addModal]);

  const canAddOwner = useMemo(() => {
    if (!addModal) return false;
    return !addModal.members.some((m) => normalizeRoleCategory(m.roleInFlat) === 'owner');
  }, [addModal]);

  const selectedFlatDues = useMemo(() => {
    if (!selectedFlat || !flatBalances) return null;
    const bals = flatBalances.get(selectedFlat.id) ?? [];
    const totalDue = bals.reduce((s, b) => s + (b.total_due || 0), 0);
    return { totalDue, categories: bals.filter((b) => b.total_due > 0) };
  }, [selectedFlat, flatBalances]);

  const financeStats = useMemo(() => {
    if (!flatBalances || !data?.flats) {
      return { totalOutstanding: 0, flatsWithDues: 0, defaulters: [] as FlatDueSummary[] };
    }
    let totalOutstanding = 0;
    let flatsWithDues = 0;
    const defaulters: FlatDueSummary[] = [];

    for (const flat of data.flats) {
      const bals = flatBalances.get(flat.id) ?? [];
      const totalDue = bals.reduce((s, b) => s + (b.total_due || 0), 0);
      totalOutstanding += totalDue;
      if (totalDue > 0) {
        flatsWithDues += 1;
        const dueCategories = bals.filter((b) => b.total_due > 0).map((b) => b.bill_type);
        const oldestDates = bals
          .filter((b) => b.oldest_unpaid_due_date)
          .map((b) => b.oldest_unpaid_due_date as string)
          .sort();
        defaulters.push({
          flat,
          totalDue,
          oldestDueDate: oldestDates[0] ?? null,
          categories: dueCategories,
        });
      }
    }

    defaulters.sort((a, b) => b.totalDue - a.totalDue);
    return { totalOutstanding, flatsWithDues, defaulters };
  }, [flatBalances, data]);

  const pendingStats = useMemo(() => {
    const count = pendingPayments.length;
    const amount = pendingPayments.reduce((s, g) => s + g.total_amount_paid, 0);
    return { count, amount };
  }, [pendingPayments]);

  const collectionPct = useMemo(() => {
    if (!maintenanceTracking) return null;
    const { totalMaintenanceAmount, paidAmount, totalUsers, paidUsers, notApplicableUsers } =
      maintenanceTracking;
    if (totalMaintenanceAmount > 0) {
      return Math.round((paidAmount / totalMaintenanceAmount) * 100);
    }
    const applicable = totalUsers - notApplicableUsers;
    if (applicable <= 0) return null;
    return Math.round((paidUsers / applicable) * 100);
  }, [maintenanceTracking]);

  const topDefaulters = useMemo(() => financeStats.defaulters.slice(0, 10), [financeStats.defaulters]);

  const reportRangeInvalid =
    !reportRange.startDate ||
    !reportRange.endDate ||
    reportRange.startDate > reportRange.endDate;

  const runBillsReport = async (format: 'pdf' | 'excel') => {
    if (!societyId || reportRangeInvalid) return;
    setReportError('');
    setReportBusy(format);
    try {
      await downloadSocietyBillsReportFile(societyId, reportRange.startDate, reportRange.endDate, format);
    } catch (e: any) {
      setReportError(e.message || 'Could not download bills report.');
    } finally {
      setReportBusy(null);
    }
  };

  const runAuditReport = async (format: 'pdf' | 'excel') => {
    if (!societyId) return;
    setReportError('');
    setReportBusy(`audit-${format}`);
    try {
      await downloadSocietyAuditReportFile(
        societyId,
        format,
        reportRangeInvalid ? undefined : reportRange.startDate,
        reportRangeInvalid ? undefined : reportRange.endDate
      );
    } catch (e: any) {
      setReportError(e.message || 'Could not download audit report.');
    } finally {
      setReportBusy(null);
    }
  };

  const downloadAllFlatsLedgerBalancesCsv = async () => {
    if (!societyId || !data?.flats?.length) return;
    setReportError('');
    setReportBusy('ledger-csv');
    try {
      const balances = flatBalances ?? (await fetchAllFlatBalances(societyId));
      if (!flatBalances) setFlatBalances(balances);

      const rows: string[][] = [
        [
          'block',
          'flat_number',
          'flat_id',
          'bill_type',
          'principal_outstanding',
          'interest_outstanding',
          'advance_balance',
          'interest_exempt_principal',
          'total_due',
          'oldest_unpaid_due_date',
        ],
      ];
      for (const flat of data.flats) {
        const bals = balances.get(flat.id) ?? [];
        if (!bals.length) {
          rows.push([flat.block_name ?? '', flat.flat_number, String(flat.id), '—', '', '', '', '', '', '']);
          continue;
        }
        for (const b of bals) {
          rows.push([
            flat.block_name ?? '',
            flat.flat_number,
            String(flat.id),
            b.bill_type,
            String(b.principal_outstanding),
            String(b.interest_outstanding),
            String(b.advance_balance),
            String(b.interest_exempt_principal),
            String(b.total_due),
            b.oldest_unpaid_due_date ?? '',
          ]);
        }
      }
      const bom = '\uFEFF';
      const body = rows.map((r) => r.map(csvEscapeCell).join(',')).join('\r\n');
      const blob = new Blob([bom + body], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `society-${societyId}-all-flats-ledger-balances-${stamp}.csv`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setReportError(e.message || 'Could not build ledger export.');
    } finally {
      setReportBusy(null);
    }
  };

  const monthLabel = useMemo(() => {
    const now = new Date();
    return now.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  }, []);

  return (
    <div className="overview-page">
      <div className="overview-top-bar" style={{ justifyContent: 'flex-end' }}>
        <button
          className="recon-btn recon-btn-outline"
          onClick={() => void refreshAll()}
          disabled={loading}
        >
          <HiRefresh size={16} /> Refresh
        </button>
      </div>

      {error && (
        <div className="recon-alert recon-alert-error" style={{ marginBottom: '20px' }}>
          <HiExclamation size={18} /> {error}
        </div>
      )}

      {loading && (
        <div className="overview-loading">
          <HiRefresh size={32} className="spin" />
          <p>Loading society data...</p>
        </div>
      )}

      {!loading && data && (
        <div className="overview-content">
          <div className="overview-summary">
            <div className="overview-kpi">
              <span className="kpi-value">{data.flats.length}</span>
              <span className="kpi-label">Total Flats</span>
            </div>
            <div className="overview-kpi kpi-registered">
              <span className="kpi-value">
                {data.flats.filter((f) => f.users && f.users.length > 0).length}
              </span>
              <span className="kpi-label">Registered Flats</span>
            </div>
            <div className="overview-kpi">
              <span className="kpi-value">
                {data.flats.reduce((sum, f) => sum + (f.users?.length || 0), 0)}
              </span>
              <span className="kpi-label">Registered Residents</span>
            </div>
          </div>

          <div className="overview-section-title">Collections</div>
          <div className="overview-summary overview-finance-kpis">
            <div className="overview-kpi kpi-finance">
              <span className="kpi-value">
                {financeLoading ? '…' : formatCurrency(financeStats.totalOutstanding)}
              </span>
              <span className="kpi-label">Total Outstanding</span>
            </div>
            <div className="overview-kpi kpi-finance kpi-warning">
              <span className="kpi-value">{financeLoading ? '…' : financeStats.flatsWithDues}</span>
              <span className="kpi-label">Flats with Dues</span>
            </div>
            <div className="overview-kpi kpi-finance">
              <span className="kpi-value">
                {financeLoading ? '…' : pendingStats.count}
              </span>
              <span className="kpi-label">Pending Approvals</span>
              {!financeLoading && pendingStats.count > 0 && (
                <span className="kpi-sub">{formatCurrency(pendingStats.amount)}</span>
              )}
            </div>
            <div className="overview-kpi kpi-finance kpi-collection">
              <span className="kpi-value">
                {financeLoading ? '…' : collectionPct != null ? `${collectionPct}%` : '—'}
              </span>
              <span className="kpi-label">Collection ({monthLabel})</span>
            </div>
          </div>

          <div className="overview-action-chips">
            {pendingStats.count > 0 && (
              <Link
                to="/society/bill-payments"
                className="overview-action-chip overview-action-chip-warning"
              >
                {pendingStats.count} pending payment{pendingStats.count !== 1 ? 's' : ''} — Review
              </Link>
            )}
            {reconciliationEnabled && (
              <Link to="/society/reconciliation" className="overview-action-chip">
                Reconcile bank statement
              </Link>
            )}
            <Link to="/society/record-payment" className="overview-action-chip">
              Record counter payment
            </Link>
            <Link to="/society/scheduled-bills" className="overview-action-chip">
              View scheduled bills
            </Link>
          </div>

          {topDefaulters.length > 0 && (
            <section className="overview-defaulters" aria-labelledby="overview-defaulters-heading">
              <h3 id="overview-defaulters-heading" className="overview-defaulters-title">
                Top defaulters
              </h3>
              <p className="overview-defaulters-desc">
                Flats with the highest outstanding dues. Click a row to open flat history.
              </p>
              <div className="overview-defaulters-table-wrap">
                <table className="overview-defaulters-table">
                  <thead>
                    <tr>
                      <th>Block</th>
                      <th>Flat</th>
                      <th>Total due</th>
                      <th>Oldest due date</th>
                      <th>Categories</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topDefaulters.map((d) => (
                      <tr
                        key={d.flat.id}
                        className="overview-defaulters-row"
                        onClick={() => navigate(`/society/flat-history?flatId=${d.flat.id}`)}
                      >
                        <td>{d.flat.block_name || '—'}</td>
                        <td>{d.flat.flat_number}</td>
                        <td className="overview-defaulters-amount">{formatCurrency(d.totalDue)}</td>
                        <td>{d.oldestDueDate ?? '—'}</td>
                        <td>{d.categories.join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="overview-reports" aria-labelledby="overview-reports-heading">
            <h3 id="overview-reports-heading" className="overview-reports-title">
              <HiDownload size={20} />
              Reports
            </h3>
            <p className="overview-reports-desc">
              Download society bills or audit reports for a date range (PDF or Excel). Export current
              category-wise ledger balances for every flat as CSV (point-in-time snapshot).
            </p>
            <div className="overview-reports-row">
              <label className="overview-reports-field">
                <span>From</span>
                <input
                  type="date"
                  value={reportRange.startDate}
                  onChange={(e) =>
                    setReportRange((r) => ({ ...r, startDate: e.target.value }))
                  }
                />
              </label>
              <label className="overview-reports-field">
                <span>To</span>
                <input
                  type="date"
                  value={reportRange.endDate}
                  onChange={(e) =>
                    setReportRange((r) => ({ ...r, endDate: e.target.value }))
                  }
                />
              </label>
            </div>
            <div className="overview-reports-actions">
              <button
                type="button"
                className="recon-btn recon-btn-primary"
                disabled={!!reportBusy || reportRangeInvalid}
                onClick={() => void runBillsReport('pdf')}
              >
                {reportBusy === 'pdf' ? 'Preparing…' : 'Bills PDF'}
              </button>
              <button
                type="button"
                className="recon-btn recon-btn-outline"
                disabled={!!reportBusy || reportRangeInvalid}
                onClick={() => void runBillsReport('excel')}
              >
                {reportBusy === 'excel' ? 'Preparing…' : 'Bills Excel'}
              </button>
              <button
                type="button"
                className="recon-btn recon-btn-outline"
                disabled={!!reportBusy}
                onClick={() => void runAuditReport('pdf')}
              >
                {reportBusy === 'audit-pdf' ? 'Preparing…' : 'Audit PDF'}
              </button>
              <button
                type="button"
                className="recon-btn recon-btn-outline"
                disabled={!!reportBusy}
                onClick={() => void runAuditReport('excel')}
              >
                {reportBusy === 'audit-excel' ? 'Preparing…' : 'Audit Excel'}
              </button>
              <button
                type="button"
                className="recon-btn recon-btn-outline"
                disabled={!!reportBusy || !data.flats.length}
                onClick={() => void downloadAllFlatsLedgerBalancesCsv()}
              >
                {reportBusy === 'ledger-csv' ? 'Building…' : 'All flats ledger (CSV)'}
              </button>
            </div>
            {reportRangeInvalid && (
              <p className="overview-reports-warn">Choose a valid date range (from ≤ to).</p>
            )}
            {reportError && (
              <div className="recon-alert recon-alert-error overview-reports-alert">
                <HiExclamation size={18} /> {reportError}
              </div>
            )}
          </section>

          <div className="overview-flats-section">
            <div className="overview-flats-section-header">
              <div className="overview-section-title">Flats</div>
              <div className="overview-flats-fix-buttons">
                <button
                  type="button"
                  className="overview-duplicate-flats-btn"
                  onClick={openDuplicateModal}
                >
                  Fix duplicate flats
                </button>
                <button
                  type="button"
                  className="overview-compound-flats-btn"
                  onClick={openCompoundModal}
                >
                  Fix combined flats
                </button>
              </div>
            </div>
            <div className="overview-flat-filters" role="tablist" aria-label="Filter flats">
              <button
                type="button"
                className={`overview-flat-filter-btn ${flatFilter === 'all' ? 'active' : ''}`}
                onClick={() => setFlatFilter('all')}
              >
                All ({flatFilterCounts.all})
              </button>
              <button
                type="button"
                className={`overview-flat-filter-btn overview-flat-filter-owner ${flatFilter === 'with_owner' ? 'active' : ''}`}
                onClick={() => setFlatFilter('with_owner')}
              >
                With Owner ({flatFilterCounts.with_owner})
              </button>
              <button
                type="button"
                className={`overview-flat-filter-btn overview-flat-filter-tenant ${flatFilter === 'with_tenant' ? 'active' : ''}`}
                onClick={() => setFlatFilter('with_tenant')}
              >
                With Tenant ({flatFilterCounts.with_tenant})
              </button>
              <button
                type="button"
                className={`overview-flat-filter-btn overview-flat-filter-vacant ${flatFilter === 'vacant' ? 'active' : ''}`}
                onClick={() => setFlatFilter('vacant')}
              >
                Vacant ({flatFilterCounts.vacant})
              </button>
            </div>

            {totalFilteredFlats === 0 ? (
              <p className="overview-flats-empty">No flats match this filter.</p>
            ) : (
              <div className="blocks-container">
                {Object.entries(groupedFlats).map(([block, flats]) => (
                  <div key={block} className="block-section">
                    <h3 className="block-title">Block {block}</h3>
                    <div className="flats-grid">
                      {flats.map((flat) => {
                        const isRegistered = flat.users && flat.users.length > 0;
                        const hasOwner = flatHasOwner(flat);
                        const hasCoOwner = flatHasCoOwner(flat);
                        const hasOwnerAndCoOwner = flatHasOwnerAndCoOwner(flat);
                        const hasTenantOnly = flatHasTenantOnly(flat);
                        const hasTenant = flatHasTenant(flat);
                        const cellClass = [
                          'flat-cell',
                          isRegistered ? 'flat-registered' : 'flat-vacant',
                          hasOwnerAndCoOwner ? 'flat-has-owner-coowner' : '',
                          hasTenantOnly ? 'flat-has-tenant-only' : '',
                          hasOwner ? 'flat-has-owner' : '',
                          hasTenant && !hasTenantOnly ? 'flat-has-tenant' : '',
                        ]
                          .filter(Boolean)
                          .join(' ');
                        const tileTitle = hasOwnerAndCoOwner
                          ? 'Owner and co-owner both assigned — review membership'
                          : hasTenantOnly
                            ? 'Tenant only — no owner assigned'
                            : 'View flat details and manage members';
                        return (
                          <div
                            key={flat.id}
                            className={cellClass}
                            onClick={() => openFlatModal(flat)}
                            title={tileTitle}
                          >
                            <span className="flat-number">{flat.flat_number}</span>
                            {isRegistered && (
                              <span className="flat-users-count">
                                <HiUsers size={12} />
                                {flat.users.length}
                              </span>
                            )}
                            {(hasOwner || hasCoOwner || hasTenant) && (
                              <span className="flat-role-tags">
                                {hasOwner && <span className="flat-role-tag flat-role-tag-owner">O</span>}
                                {hasCoOwner && <span className="flat-role-tag flat-role-tag-coowner">C</span>}
                                {hasTenant && <span className="flat-role-tag flat-role-tag-tenant">T</span>}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedFlat && (
        <div className="overview-modal-overlay" onClick={closeFlatModal}>
          <div className="overview-modal overview-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="overview-modal-header">
              <h3>
                Flat {selectedFlat.block_name ? `${selectedFlat.block_name}-` : ''}
                {selectedFlat.flat_number}
              </h3>
              <button type="button" className="overview-modal-close" onClick={closeFlatModal}>
                <HiX size={20} />
              </button>
            </div>
            <div className="overview-modal-body">
              <div className="flat-detail-info">
                <p>
                  <strong>Block:</strong> {selectedFlat.block_name || '—'}
                </p>
                <p>
                  <strong>Floor:</strong> {selectedFlat.floor ?? '—'}
                </p>
                <p>
                  <strong>Area:</strong> {selectedFlat.area ? `${selectedFlat.area} sq.ft` : '—'}
                </p>
                <p>
                  <strong>Occupancy:</strong>{' '}
                  {flatHasOwner(selectedFlat) && <span className="recon-badge badge-blue">Owner</span>}{' '}
                  {flatHasTenant(selectedFlat) && <span className="recon-badge badge-green">Tenant</span>}{' '}
                  {!flatHasOwner(selectedFlat) && !flatHasTenant(selectedFlat) && (
                    <span className="recon-badge badge-yellow">Vacant</span>
                  )}
                </p>
                {selectedFlatDues && selectedFlatDues.totalDue > 0 && (
                  <p>
                    <strong>Outstanding:</strong>{' '}
                    <span className="overview-flat-due">{formatCurrency(selectedFlatDues.totalDue)}</span>
                  </p>
                )}
              </div>

              <div className="overview-modal-toolbar">
                <button type="button" className="saflats-add-btn" onClick={openAddModal}>
                  <HiPlus size={14} />
                  Add member
                </button>
                <button
                  type="button"
                  className="recon-btn recon-btn-outline overview-modal-history-btn"
                  onClick={() => navigate(`/society/flat-history?flatId=${selectedFlat.id}`)}
                >
                  Flat history
                </button>
                <button
                  type="button"
                  className="recon-btn recon-btn-outline"
                  onClick={() => void loadModalMembers(selectedFlat.id)}
                  disabled={modalMembersLoading}
                >
                  <HiRefresh size={14} className={modalMembersLoading ? 'spin' : ''} />
                  Refresh
                </button>
              </div>

              <h4 className="flat-users-title">Members ({modalMembers.length})</h4>

              {modalMembersLoading && (
                <div className="saflats-panel-loading">
                  <HiRefresh size={16} className="saflats-spin" /> Loading members...
                </div>
              )}
              {modalMembersError && (
                <div className="saflats-panel-error">
                  <HiExclamation size={16} /> {modalMembersError}
                </div>
              )}
              {!modalMembersLoading && !modalMembersError && modalMembers.length === 0 && (
                <p className="no-users-text">No members linked to this flat. Add a resident using the button above.</p>
              )}
              {!modalMembersLoading && modalMembers.length > 0 && (
                <div className="saflats-members-grid overview-modal-members">
                  {modalMembers.map((m) => {
                    const cat = normalizeRoleCategory(m.roleInFlat);
                    return (
                      <div key={m.userFlatId} className="saflats-member-card">
                        <div className="saflats-member-top">
                          <div className="saflats-member-info">
                            <span className="saflats-member-name">{m.name}</span>
                            <div className="saflats-member-badges">
                              <span className={`saflats-role-badge saflats-role-${cat}`}>
                                {roleBadgeLabel(cat)}
                              </span>
                              {m.isPrimaryTenant && (
                                <span className="saflats-primary-badge">Primary</span>
                              )}
                              <span
                                className={`saflats-approval-badge ${m.isApproved ? 'saflats-approved' : 'saflats-pending'}`}
                              >
                                {m.isApproved ? 'Approved' : 'Pending'}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="saflats-remove-btn"
                            title="Remove member"
                            onClick={() => void openRemovalModal(m)}
                          >
                            <HiTrash size={14} />
                          </button>
                        </div>
                        <div className="saflats-member-details">
                          {m.phone && <span>{m.phone}</span>}
                          {m.email && <span>{m.email}</span>}
                          {m.joinedAt && <span>Joined {formatDate(m.joinedAt)}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {addModal && (
        <div className="saflats-modal-overlay" onClick={() => !addModal.submitting && setAddModal(null)}>
          <div className="saflats-modal saflats-modal--add" onClick={(e) => e.stopPropagation()}>
            <div className="saflats-modal-header saflats-modal-header--add">
              <h3>Add Member</h3>
              <button
                type="button"
                className="saflats-modal-close"
                onClick={() => !addModal.submitting && setAddModal(null)}
                disabled={addModal.submitting}
              >
                <HiX size={18} />
              </button>
            </div>
            <div className="saflats-modal-body">
              <p className="saflats-modal-flat-label">
                Flat {addModal.flat.flat_number}
                {addModal.flat.block_name ? ` · Block ${addModal.flat.block_name}` : ''}
              </p>
              <p className="saflats-modal-note">Creates or links a user to this flat. The resident will not be notified.</p>
              {addModal.error && (
                <div className="saflats-modal-error">
                  <HiExclamation size={18} />
                  <span>{addModal.error}</span>
                </div>
              )}
              <div className="saflats-form-field">
                <label htmlFor="overview-add-phone">Phone</label>
                <input
                  id="overview-add-phone"
                  type="tel"
                  className="saflats-form-input"
                  placeholder="10-digit mobile number"
                  value={addModal.phone}
                  onChange={(e) =>
                    setAddModal((prev) => (prev ? { ...prev, phone: e.target.value, error: '' } : null))
                  }
                  disabled={addModal.submitting}
                />
              </div>
              <div className="saflats-form-field">
                <label htmlFor="overview-add-name">Full name</label>
                <input
                  id="overview-add-name"
                  type="text"
                  className="saflats-form-input"
                  placeholder="Resident name"
                  value={addModal.name}
                  onChange={(e) =>
                    setAddModal((prev) => (prev ? { ...prev, name: e.target.value, error: '' } : null))
                  }
                  disabled={addModal.submitting}
                />
              </div>
              <div className="saflats-form-field">
                <label htmlFor="overview-add-role">Role</label>
                <select
                  id="overview-add-role"
                  className="saflats-form-input"
                  value={addModal.roleInFlat}
                  onChange={(e) => {
                    const roleInFlat = e.target.value as FlatMemberRole;
                    setAddModal((prev) =>
                      prev
                        ? {
                            ...prev,
                            roleInFlat,
                            linkedToUserId: roleInFlat === 'family_member' ? prev.linkedToUserId : '',
                            error: '',
                          }
                        : null,
                    );
                  }}
                  disabled={addModal.submitting}
                >
                  {canAddOwner && <option value="owner">Owner</option>}
                  <option value="co_owner">Co-owner</option>
                  <option value="tenant">Tenant</option>
                  <option value="family_member">Family member</option>
                </select>
              </div>
              {addModal.roleInFlat === 'family_member' && (
                <div className="saflats-form-field">
                  <label htmlFor="overview-add-linked">Linked to</label>
                  <select
                    id="overview-add-linked"
                    className="saflats-form-input"
                    value={addModal.linkedToUserId}
                    onChange={(e) =>
                      setAddModal((prev) =>
                        prev
                          ? {
                              ...prev,
                              linkedToUserId: e.target.value === '' ? '' : Number(e.target.value),
                              error: '',
                            }
                          : null,
                      )
                    }
                    disabled={addModal.submitting || linkableMembers.length === 0}
                  >
                    <option value="">Select owner or tenant</option>
                    {linkableMembers.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name} ({roleBadgeLabel(normalizeRoleCategory(m.roleInFlat))})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="saflats-modal-actions">
                <button
                  type="button"
                  className="saflats-btn saflats-btn-cancel"
                  onClick={() => setAddModal(null)}
                  disabled={addModal.submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="saflats-btn saflats-btn-primary"
                  onClick={() => void submitAddMember()}
                  disabled={
                    addModal.submitting ||
                    (addModal.roleInFlat === 'family_member' && linkableMembers.length === 0)
                  }
                >
                  {addModal.submitting ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {removalModal && (
        <div className="saflats-modal-overlay" onClick={() => !removalModal.removing && setRemovalModal(null)}>
          <div className="saflats-modal" onClick={(e) => e.stopPropagation()}>
            <div className="saflats-modal-header">
              <h3>Remove Member</h3>
              <button
                type="button"
                className="saflats-modal-close"
                onClick={() => !removalModal.removing && setRemovalModal(null)}
                disabled={removalModal.removing}
              >
                <HiX size={18} />
              </button>
            </div>
            <div className="saflats-modal-body">
              {removalModal.checkLoading && (
                <div className="saflats-modal-loading">
                  <HiRefresh size={20} className="saflats-spin" />
                  <span>Checking removal impact...</span>
                </div>
              )}
              {removalModal.checkError && (
                <div className="saflats-modal-error">
                  <HiExclamation size={18} />
                  <span>{removalModal.checkError}</span>
                </div>
              )}
              {removalModal.checkData && (
                <>
                  <div className="saflats-modal-member-info">
                    <p>
                      <strong>{removalModal.checkData.name}</strong> ({removalModal.checkData.roleInFlat})
                    </p>
                    <p>Flat {removalModal.checkData.flatNumber}</p>
                  </div>
                  {removalModal.checkData.pendingBillCount > 0 && (
                    <div className="saflats-modal-warning">
                      <HiExclamation size={16} />
                      <div>
                        <strong>{removalModal.checkData.pendingBillCount} pending bill(s)</strong> totalling{' '}
                        {formatRupees(removalModal.checkData.pendingBillTotal)}.
                      </div>
                    </div>
                  )}
                  {removalModal.checkData.cascadeMemberCount > 0 && (
                    <div className="saflats-modal-warning">
                      <HiExclamation size={16} />
                      <div>
                        <strong>{removalModal.checkData.cascadeMemberCount} linked member(s)</strong> will also be
                        removed.
                      </div>
                    </div>
                  )}
                  <div className="saflats-modal-reason">
                    <label htmlFor="overview-removal-reason">Reason (optional)</label>
                    <textarea
                      id="overview-removal-reason"
                      className="saflats-reason-input"
                      value={removalModal.reason}
                      onChange={(e) =>
                        setRemovalModal((prev) => (prev ? { ...prev, reason: e.target.value } : null))
                      }
                      rows={3}
                    />
                  </div>
                  <div className="saflats-modal-actions">
                    <button
                      type="button"
                      className="saflats-btn saflats-btn-cancel"
                      onClick={() => setRemovalModal(null)}
                      disabled={removalModal.removing}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="saflats-btn saflats-btn-danger"
                      onClick={() => void confirmRemoval()}
                      disabled={removalModal.removing}
                    >
                      {removalModal.removing ? 'Removing...' : 'Remove Member'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {duplicateModalOpen && (
        <div
          className="overview-modal-overlay"
          onClick={() => !mergingMasterFlatId && !mergingAll && setDuplicateModalOpen(false)}
        >
          <div className="overview-duplicate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="overview-duplicate-modal-header">
              <div>
                <h3>Duplicate flat preview</h3>
                <p className="overview-duplicate-modal-sub">
                  Actual canonical flats vs corrupted variants (e.g. A-101 vs A101, A 101, 101A)
                </p>
              </div>
              <button
                type="button"
                className="saflats-modal-close"
                onClick={() => !mergingMasterFlatId && !mergingAll && setDuplicateModalOpen(false)}
                disabled={!!mergingMasterFlatId || mergingAll}
              >
                <HiX size={18} />
              </button>
            </div>

            <div className="overview-duplicate-modal-toolbar">
              {duplicateGroups.length > 0 && (
                <button
                  type="button"
                  className="overview-duplicate-merge-all-btn"
                  disabled={duplicateLoading || !!mergingMasterFlatId || mergingAll}
                  onClick={() => void handleMergeAllDuplicateGroups()}
                >
                  {mergingAll ? 'Merging all…' : `Merge all (${duplicateGroups.length})`}
                </button>
              )}
              <button
                type="button"
                className="recon-btn recon-btn-outline"
                onClick={() => void loadDuplicatePreview()}
                disabled={duplicateLoading || !!mergingMasterFlatId || mergingAll}
              >
                {duplicateLoading ? 'Refreshing…' : 'Refresh preview'}
              </button>
            </div>

            {duplicateError && (
              <div className="recon-alert recon-alert-error overview-duplicate-alert">
                <HiExclamation size={18} /> {duplicateError}
              </div>
            )}
            {mergeSuccessMessage && (
              <div className="overview-duplicate-success">{mergeSuccessMessage}</div>
            )}

            <div className="overview-duplicate-modal-body">
              {duplicateLoading ? (
                <div className="overview-duplicate-loading">
                  <HiRefresh size={20} className="saflats-spin" />
                  <span>Scanning flats…</span>
                </div>
              ) : duplicateGroups.length === 0 ? (
                <p className="overview-duplicate-empty">No duplicate flat groups found. All flat numbers look consistent.</p>
              ) : (
                duplicateGroups.map((group) => (
                  <div key={group.groupKey} className="overview-duplicate-group">
                    <div className="overview-duplicate-group-head">
                      <div>
                        <strong>Block {group.blockName}</strong>
                        <span className="overview-duplicate-canonical">Actual: {group.canonical}</span>
                      </div>
                      <button
                        type="button"
                        className="overview-duplicate-merge-btn"
                        disabled={!!mergingMasterFlatId || mergingAll}
                        onClick={() => void handleMergeDuplicateGroup(group.master.id)}
                      >
                        {mergingMasterFlatId === group.master.id ? 'Merging…' : 'Merge this flat'}
                      </button>
                    </div>

                    {group.warnings.map((w) => (
                      <div key={w} className="overview-duplicate-warning">
                        <HiExclamation size={14} /> {w}
                      </div>
                    ))}

                    <div className="overview-duplicate-columns">
                      <div className="overview-duplicate-col overview-duplicate-col-master">
                        <div className="overview-duplicate-col-title">Actual flat (keep)</div>
                        <div className="overview-duplicate-flat-card is-master">
                          <div className="overview-duplicate-flat-name">{group.master.flatNumber}</div>
                          <div className="overview-duplicate-flat-meta">{group.master.memberCount} member(s)</div>
                          {group.master.members.map((m) => (
                            <div key={`${group.master.id}-${m.userId}`} className="overview-duplicate-member">
                              {m.name} · {m.role}
                              {m.vehicles.length > 0 && (
                                <span className="overview-duplicate-vehicles"> · {m.vehicles.join(', ')}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="overview-duplicate-col">
                        <div className="overview-duplicate-col-title">
                          Corrupted variant{group.corrupted.length > 1 ? 's' : ''} (merge into actual)
                        </div>
                        {group.corrupted.map((flat) => (
                          <div key={flat.id} className="overview-duplicate-flat-card is-corrupted">
                            <div className="overview-duplicate-flat-name">{flat.flatNumber}</div>
                            <div className="overview-duplicate-flat-meta">{flat.memberCount} member(s)</div>
                            {flat.members.length === 0 ? (
                              <div className="overview-duplicate-member muted">No members</div>
                            ) : (
                              flat.members.map((m) => (
                                <div key={`${flat.id}-${m.userId}`} className="overview-duplicate-member">
                                  {m.name} · {m.role}
                                  {m.vehicles.length > 0 && (
                                    <span className="overview-duplicate-vehicles"> · {m.vehicles.join(', ')}</span>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {group.actions.length > 0 && (
                      <div className="overview-duplicate-actions">
                        <div className="overview-duplicate-actions-title">Merge plan</div>
                        <ul>
                          {group.actions.map((a) => (
                            <li key={`${a.userId}-${a.action}-${a.sourceRole}`}>
                              {a.userName}: {formatMergeAction(a.action)}
                              {a.targetRole ? ` → ${a.targetRole.replace(/_/g, ' ')}` : ''}
                              {a.vehicles.length > 0 ? ` (vehicles: ${a.vehicles.join(', ')})` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {compoundModalOpen && (
        <div
          className="overview-modal-overlay"
          onClick={() => !splittingCompoundFlatId && !splittingAllCompounds && setCompoundModalOpen(false)}
        >
          <div className="overview-duplicate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="overview-duplicate-modal-header">
              <div>
                <h3>Combined flat preview</h3>
                <p className="overview-duplicate-modal-sub">
                  Split multi-unit entries like 129,130 or 629.630 into separate master flats (A-129, A-130, …)
                </p>
              </div>
              <button
                type="button"
                className="saflats-modal-close"
                onClick={() => !splittingCompoundFlatId && !splittingAllCompounds && setCompoundModalOpen(false)}
                disabled={!!splittingCompoundFlatId || splittingAllCompounds}
              >
                <HiX size={18} />
              </button>
            </div>

            <div className="overview-duplicate-modal-toolbar">
              {compoundFlats.filter((c) => c.targets.every((t) => t.found)).length > 0 && (
                <button
                  type="button"
                  className="overview-duplicate-merge-all-btn"
                  disabled={compoundLoading || !!splittingCompoundFlatId || splittingAllCompounds}
                  onClick={() => void handleSplitAllCompoundFlats()}
                >
                  {splittingAllCompounds
                    ? 'Splitting all…'
                    : `Split all (${compoundFlats.filter((c) => c.targets.every((t) => t.found)).length})`}
                </button>
              )}
              <button
                type="button"
                className="recon-btn recon-btn-outline"
                onClick={() => void loadCompoundPreview()}
                disabled={compoundLoading || !!splittingCompoundFlatId || splittingAllCompounds}
              >
                {compoundLoading ? 'Refreshing…' : 'Refresh preview'}
              </button>
            </div>

            {compoundError && (
              <div className="recon-alert recon-alert-error overview-duplicate-alert">
                <HiExclamation size={18} /> {compoundError}
              </div>
            )}
            {compoundSuccessMessage && (
              <div className="overview-duplicate-success">{compoundSuccessMessage}</div>
            )}

            <div className="overview-duplicate-modal-body">
              {compoundLoading ? (
                <div className="overview-duplicate-loading">
                  <HiRefresh size={20} className="saflats-spin" />
                  <span>Scanning flats…</span>
                </div>
              ) : compoundFlats.length === 0 ? (
                <p className="overview-duplicate-empty">No combined flat numbers found.</p>
              ) : (
                compoundFlats.map((item) => {
                  const canSplit = item.targets.every((t) => t.found);
                  return (
                    <div key={item.compoundFlatId} className="overview-duplicate-group">
                      <div className="overview-duplicate-group-head">
                        <div>
                          <strong>Block {item.blockName}</strong>
                          <span className="overview-duplicate-canonical overview-compound-bad">
                            Combined: {item.compoundFlatNumber}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="overview-duplicate-merge-btn"
                          disabled={!canSplit || !!splittingCompoundFlatId || splittingAllCompounds}
                          onClick={() => void handleSplitCompoundFlat(item.compoundFlatId)}
                        >
                          {splittingCompoundFlatId === item.compoundFlatId ? 'Splitting…' : 'Split this flat'}
                        </button>
                      </div>

                      {item.warnings.map((w) => (
                        <div key={w} className="overview-duplicate-warning">
                          <HiExclamation size={14} /> {w}
                        </div>
                      ))}

                      <div className="overview-duplicate-columns">
                        <div className="overview-duplicate-col overview-duplicate-col-master">
                          <div className="overview-duplicate-col-title">Wrong combined flat</div>
                          <div className="overview-duplicate-flat-card is-corrupted">
                            <div className="overview-duplicate-flat-name">{item.compoundFlatNumber}</div>
                            <div className="overview-duplicate-flat-meta">{item.members.length} member(s)</div>
                            {item.members.map((m) => (
                              <div key={`${item.compoundFlatId}-${m.userId}`} className="overview-duplicate-member">
                                {m.name} · {m.role}
                                {m.vehicles.length > 0 && (
                                  <span className="overview-duplicate-vehicles"> · {m.vehicles.join(', ')}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="overview-duplicate-col">
                          <div className="overview-duplicate-col-title">Split into master flats</div>
                          {item.targets.map((t) => (
                            <div
                              key={t.canonical}
                              className={`overview-duplicate-flat-card ${t.found ? 'is-master' : 'is-corrupted'}`}
                            >
                              <div className="overview-duplicate-flat-name">{t.canonical}</div>
                              <div className="overview-duplicate-flat-meta">
                                {t.found ? t.masterFlatNumber : 'Master flat not found'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {item.actions.length > 0 && (
                        <div className="overview-duplicate-actions">
                          <div className="overview-duplicate-actions-title">Split plan</div>
                          <ul>
                            {item.actions.map((a, idx) => (
                              <li key={`${a.userId}-${a.targetCanonical}-${idx}`}>
                                {a.userName}: {formatCompoundAction(a.action)} → {a.targetCanonical}
                                {a.targetRole ? ` (${a.targetRole.replace(/_/g, ' ')})` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocietyAdminOverview;
